import { subDays } from 'date-fns';
import { format } from 'date-fns/format';
import { useState } from 'react';
import { useDeleteWorkScheduleSlotRecurrenceForm } from '@/components/forms/delete-work-schedule-slot-recurrence/use-delete-work-schedule-slot-recurrence-form';
import useDeleteOccurrenceWorkScheduleSlotMutation, {
  DeleteOccurrenceWorkScheduleSlotResponse,
} from '@/hooks/mutations/use-delete-occurrence-work-schedule-slot';
import useStopRecurrenceWorkScheduleSlotMutation, {
  StopRecurrenceWorkScheduleSlotResponse,
} from '@/hooks/mutations/use-stop-recurrence-work-schedule-slot';
import { WorkScheduleSlotSequence } from '@/hooks/queries/use-get-office-work-schedules-slots';
import { popModal } from '..';

export type DeleteWorkScheduleSlotRecurrenceModalApi = {
  onDeleteOccurenceSuccess: ({
    data,
  }: {
    data: DeleteOccurrenceWorkScheduleSlotResponse;
  }) => void;
  onStopSeriesSuccess: ({
    data,
  }: {
    data: StopRecurrenceWorkScheduleSlotResponse['data'];
  }) => void;
  onClose?: (props?: { deleted: boolean }) => void;
  onCancel?: () => void;
};
export type DeleteWorkScheduleSlotEventOccurence = {
  id: string | null;
  start: Date;
  end: Date | null;
  allday: boolean;
  index: number;
};
export const selectDeleteWorkScheduleSlotRecurrenceViewModel = ({
  slotId,
  sequence,
  api,
  eventOccurrence,
}: {
  slotId: string;
  sequence: WorkScheduleSlotSequence;
  api: DeleteWorkScheduleSlotRecurrenceModalApi;
  eventOccurrence: DeleteWorkScheduleSlotEventOccurence;
}) => {
  const [show, setShow] = useState<boolean>(true);
  const { deleteWorkScheduleSlotRecurrenceForm } =
    useDeleteWorkScheduleSlotRecurrenceForm();

  const {
    mutate: deleteOccurenceWorkScheduleSlotMutation,
    isPending: isDeleteOccurrenceLoading,
  } = useDeleteOccurrenceWorkScheduleSlotMutation();

  const {
    mutate: stopRecurrenceWorkScheduleSlot,
    isPending: isStopRecurrenceWorkScheduleSlotLoading,
  } = useStopRecurrenceWorkScheduleSlotMutation();

  const closeModal = (props?: { deleted: boolean }) => {
    setShow(false);
    popModal('DeleteWorkScheduleSlotRecurrenceModal');
    api.onClose?.(props);
  };

  const cancelModal = () => {
    setShow(false);
    popModal('DeleteWorkScheduleSlotRecurrenceModal');
    api.onCancel?.();
  };

  const submitDeleteOccurrence = () => {
    deleteOccurenceWorkScheduleSlotMutation(
      {
        id: slotId,
        exdate: eventOccurrence.allday
          ? format(eventOccurrence.start, 'yyyy-MM-dd')
          : eventOccurrence.start.toISOString(),
      },
      {
        onSuccess(deleteOccurenceWorkScheduleSlotResponse) {
          api.onDeleteOccurenceSuccess({
            data: deleteOccurenceWorkScheduleSlotResponse,
          });

          closeModal({ deleted: true });
        },
      },
    );
  };
  const submitStopSeries = () => {
    const untilDate = subDays(eventOccurrence.start, 1);
    stopRecurrenceWorkScheduleSlot(
      {
        id: slotId,
        sequenceId: sequence.id,
        until: eventOccurrence.allday
          ? format(untilDate, 'yyyy-MM-dd')
          : untilDate.toISOString(),
        occurrenceIndex: eventOccurrence.index,
      },
      {
        onSuccess(stopRecurrenceWorkScheduleSlotResponse) {
          api.onStopSeriesSuccess({
            data: stopRecurrenceWorkScheduleSlotResponse,
          });

          closeModal({ deleted: true });
        },
      },
    );
  };

  return {
    api: {
      submit: () => {
        const { mode: selectedDeleteMode } =
          deleteWorkScheduleSlotRecurrenceForm.getValues();

        if (selectedDeleteMode === 'delete-occurrence') {
          return submitDeleteOccurrence();
        } else if (selectedDeleteMode === 'stop-recurrence') {
          return submitStopSeries();
        }
      },
      close: closeModal,
      cancel: cancelModal,
    },
    data: {
      show,
      deleting:
        isDeleteOccurrenceLoading || isStopRecurrenceWorkScheduleSlotLoading,
      form: deleteWorkScheduleSlotRecurrenceForm,
    },
  };
};
